/**
 * /ai/runs — run history with kind filter + "start run" launcher for
 * skill-scoped kinds (eval / fleet / canary / tune / classify-eval /
 * export-finetune). Live rows keep polling while queued/running.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Heading,
  HStack,
  Text,
  Button,
  Select,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiRefreshCw, FiPlay, FiChevronDown } from 'react-icons/fi';
import { callDocai } from '../../hooks/useDocAI';
import StatusChip from '../../components/AI/StatusChip';

const KINDS = ['eval', 'fleet', 'canary', 'tune', 'classify-eval', 'export-finetune', 'process', 'bootstrap'];
const START_KINDS = [
  { kind: 'eval', label: 'Eval (goldens scorecard)', params: {} },
  { kind: 'fleet', label: 'Fleet (all documents, cached)', params: { xcheck: true } },
  { kind: 'fleet-verify', realKind: 'fleet', label: 'Fleet + verifier + LOS', params: { verify: true, xcheck: true } },
  { kind: 'canary', label: 'Canary (drift check, fresh calls)', params: { sample: 5 } },
  { kind: 'tune', label: 'Tune (propose + eval-gate prompt)', params: { rounds: 2 } },
  { kind: 'classify-eval', label: 'Classifier eval (labeled docs)', params: {} },
  { kind: 'export-finetune', label: 'Export fine-tune dataset', params: {} },
];

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');

export default function AIRunsPage() {
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [kind, setKind] = useState('');
  const [skills, setSkills] = useState([]);
  const [skillName, setSkillName] = useState('uwm-lock-confirmation');
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const history = useHistory();
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/runs$/, '');

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    callDocai('docaiSkills', { action: 'list' })
      .then((res) => setSkills((res.skills || []).filter((s) => !s.name.startsWith('_'))))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callDocai('docaiRuns', { action: 'list', kind: kind || undefined, limit: 100 });
      setRuns(res.runs || []);
      setTotal(res.total || 0);
    } catch (err) {
      toast({ title: 'Runs load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [kind, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep refreshing while anything is active.
  useEffect(() => {
    const hasActive = runs.some((r) => ['queued', 'running'].includes(r.status));
    if (!hasActive) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [runs, load]);

  const start = async (entry) => {
    try {
      const res = await callDocai('docaiRuns', {
        action: 'start',
        kind: entry.realKind || entry.kind,
        skill: skillName,
        params: entry.params,
      });
      toast({ title: `${entry.label} queued`, status: 'success', duration: 3000 });
      history.push(`${sectionUrl}/runs/${res.runId}`);
    } catch (err) {
      toast({ title: 'Start failed', description: err.message, status: 'error', duration: 8000 });
    }
  };

  const cancel = async (runId) => {
    try {
      await callDocai('docaiRuns', { action: 'cancel', runId });
      toast({ title: 'Cancel requested', status: 'info', duration: 2500 });
      load();
    } catch (err) {
      toast({ title: 'Cancel failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={4} flexWrap="wrap" spacing={3}>
        <Heading size="md">Runs</Heading>
        <HStack>
          <Select size="sm" w="240px" value={skillName} onChange={(e) => setSkillName(e.target.value)}>
            {(skills.length ? skills : [{ name: 'uwm-lock-confirmation' }]).map((s) => (
              <option key={s.name} value={s.name}>{s.name}</option>
            ))}
          </Select>
          <Menu>
            <MenuButton as={Button} size="sm" colorScheme="blue" leftIcon={<FiPlay />} rightIcon={<FiChevronDown />}>
              Start run
            </MenuButton>
            <MenuList>
              {START_KINDS.map((entry) => (
                <MenuItem key={entry.kind} onClick={() => start(entry)} fontSize="sm">
                  {entry.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={load} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </HStack>

      <HStack mb={4} spacing={3}>
        <Select size="sm" maxW="220px" placeholder="All kinds" value={kind} onChange={(e) => setKind(e.target.value)}>
          {KINDS.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </Select>
        <Text fontSize="xs" color="gray.500">{total} run{total === 1 ? '' : 's'}</Text>
      </HStack>

      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden">
        {loading && !runs.length ? (
          <Center py={16}><Spinner color="blue.400" /></Center>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr><Th>Kind</Th><Th>Skill</Th><Th>Status</Th><Th>Progress</Th><Th>By</Th><Th>Created</Th><Th /></Tr>
            </Thead>
            <Tbody>
              {runs.map((r) => (
                <Tr
                  key={r.id}
                  _hover={{ bg: cardBorder, cursor: 'pointer' }}
                  onClick={() => history.push(`${sectionUrl}/runs/${r.id}`)}
                >
                  <Td fontSize="xs" fontWeight="medium">{r.kind}</Td>
                  <Td fontSize="xs">{r.skill_name || '—'}</Td>
                  <Td><StatusChip status={r.status} /></Td>
                  <Td fontSize="xs">
                    {r.progress?.total
                      ? `${r.progress.done || 0}/${r.progress.total}${r.progress.phase ? ` · ${r.progress.phase}` : ''}`
                      : r.progress?.phase || '—'}
                  </Td>
                  <Td fontSize="xs">{r.created_by || '—'}</Td>
                  <Td fontSize="xs">{fmtDate(r.created_at)}</Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    {['queued', 'running'].includes(r.status) && (
                      <Button size="xs" variant="outline" colorScheme="red" onClick={() => cancel(r.id)}>
                        Cancel
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
              {!runs.length && (
                <Tr>
                  <Td colSpan={7}>
                    <Center py={10}><Text fontSize="sm" color="gray.500">No runs yet.</Text></Center>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>
    </Box>
  );
}
